/**
 * What a panel renders when it has no data — which, as of 2026-08-20, is every
 * panel on this board.
 *
 * This is the most important component in the feature, because the board's
 * normal appearance today is *not* `ok`. Both Log Analytics workspaces are
 * unreadable and they are unreadable in different ways: the F&O telemetry
 * workspace resolves and then answers 403 (a `Log Analytics Reader` assignment
 * is pending), while the Service Bus workspace answers 404 (the resource ID the
 * Power BI model named does not exist). Those are two different tickets for two
 * different people, so they get two visibly different panels — see `ACCESS_COPY`
 * in `./status.ts` for all six `access_state` values and their remedies (`ok`
 * plus the five ways a panel can have no data).
 *
 * The rule this component exists to enforce: **a 404 never renders as zero, and
 * never as an empty chart.** An empty axis or a `0` backlog reads as a healthy,
 * quiet system, which is the flattering direction the design rules warn about
 * (§5 "unconfigured ≠ empty ≠ zero", §8 "never let the shortfall read as a
 * healthy zero"). So the sections below this panel do not render at all while
 * the state is not `ok` — no strip of zeros, no flat line at the axis.
 *
 * Extends the `CloudSetupPanel` pattern in `features/cloud/not-configured.tsx`:
 * same outlined accent card, same "render the backend's `status_detail`
 * verbatim, never paraphrase it" discipline, widened from one unconfigured
 * state to the five distinct failures this board's `access_state` can report.
 */

import { Icon } from "@norican/icons";
import { Alert, Badge, Card, DescriptionList, Typography } from "@norican/react";

import type { FoOverview } from "@/lib/api";

import { ACCESS_COPY, formatWindow, NOT_ZERO_NOTE } from "./status";

/**
 * The state contract every `/fo/*` payload shares — the backend's `FoPanel`
 * base, projected out of the generated type rather than re-typed by hand. All
 * four payloads satisfy it structurally, so a panel component can take any of
 * them without knowing which route it came from.
 */
export type FoPanelState = Pick<
  FoOverview,
  | "configured"
  | "access_state"
  | "status_detail"
  | "collected_at"
  | "window"
  | "cache_ttl_sec"
  | "workspace"
  | "errors"
>;

/** Whether a payload carries measured numbers, as opposed to a reason it does not. */
export function isPanelOk(panel: Pick<FoPanelState, "access_state">): boolean {
  return panel.access_state === "ok";
}

interface FoAccessPanelProps {
  /** What this panel would have shown, e.g. "Front-office availability". */
  panel: string;
  /** The payload's state contract. */
  state: FoPanelState;
  /**
   * The resource ID that was queried, echoed for diagnosis. Only
   * `/fo/integration` carries one, and it is exactly what a 404 needs shown.
   */
  resourceId?: string;
  /** The Service Bus namespace, when this panel has one. */
  namespace?: string;
  /**
   * Collapse to a single line, because the same state on the same workspace has
   * already been explained in full higher up the board. Three of the four panels
   * read the same workspace, so repeating the remediation list three times would
   * read as three separate faults — the same reason the backend folds identical
   * causes into one `errors[]` line.
   */
  repeated?: boolean;
}

export function FoAccessPanel({
  panel,
  state,
  resourceId,
  namespace,
  repeated = false,
}: FoAccessPanelProps) {
  const copy = ACCESS_COPY[state.access_state];

  if (repeated) {
    return (
      <Alert
        tone={copy.tone}
        icon={<Icon icon={copy.icon} size="sm" />}
        title={`${panel} — ${copy.badge}`}
      >
        <Typography variant="body-sm" tone="muted">
          {state.access_state === "no_credential"
            ? "Same cause as above: no credential, so this panel was never asked for either."
            : `Same cause as above, on the ${state.workspace} workspace.`}{" "}
          Nothing was measured, so nothing is shown — not zero, and not an empty chart.
        </Typography>
      </Alert>
    );
  }

  const facts = [
    { label: "Panel", value: panel },
    { label: "Workspace", value: state.workspace },
    ...(namespace ? [{ label: "Service Bus namespace", value: namespace }] : []),
    ...(resourceId
      ? [
          {
            label: "Resource ID queried",
            value: <code className="text-fg text-xs break-all">{resourceId}</code>,
          },
        ]
      : []),
    { label: "Window it would cover", value: formatWindow(state.window) },
    {
      label: "Credential",
      value: state.configured
        ? "Present — NOVA authenticated successfully"
        : "Absent — nothing was asked of Azure",
    },
  ];

  return (
    <Card variant="outline" accent="left" accentTone={copy.tone}>
      <Card.Header>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center gap-3">
            <Icon icon={copy.icon} size="md" />
            <Typography variant="heading-4" as="h2">
              {copy.title}
            </Typography>
          </div>
          {/* State is never colour-alone (§3.2) — the badge names it in words. */}
          <Badge tone={copy.tone} variant="subtle" size="sm">
            {copy.badge}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col gap-4">
          {/* The backend's own one-liner, verbatim. It already names the role,
              the scope and the subscription — paraphrasing it would lose the
              exact strings an operator has to paste somewhere. */}
          <Alert tone={copy.tone} title={`Log Analytics: what happened to "${panel}"`}>
            {state.status_detail}
          </Alert>

          <Typography variant="body-sm" tone="muted">
            {copy.owner}
          </Typography>

          {copy.remedy.length > 0 && (
            <ul className="text-fg-muted flex list-disc flex-col gap-1.5 pl-5 text-sm">
              {copy.remedy.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          )}

          <DescriptionList items={facts} columns={2} />

          <Typography variant="body-sm" tone="muted">
            {NOT_ZERO_NOTE}
          </Typography>

          {state.errors.length > 0 && <FoErrorsAlert errors={state.errors} />}
        </div>
      </Card.Body>
    </Card>
  );
}

interface FoErrorsAlertProps {
  /** The payload's `errors[]` — non-fatal problems hit while collecting. */
  errors: readonly string[];
  /**
   * Heading override. The default suits the partial case (`access_state: "ok"`
   * with some queries failed), which is the one that needs the loudest label.
   */
  title?: string;
}

/**
 * `errors[]` surfaced as an `Alert` (§5), never swallowed.
 *
 * The backend already folds identical causes into one line and names the
 * affected queries, so this renders the list as-is rather than deduplicating
 * again. When it appears alongside real numbers, those numbers are a lower
 * bound — which is what the title says.
 */
export function FoErrorsAlert({ errors, title }: FoErrorsAlertProps) {
  return (
    <Alert
      tone="warning"
      title={title ?? "This snapshot is incomplete — treat the numbers as a floor"}
    >
      <ul className="flex flex-col gap-1">
        {errors.map((message) => (
          <li key={message}>{message}</li>
        ))}
      </ul>
    </Alert>
  );
}
